import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollText, Download } from "lucide-react";
import { toast } from "sonner";

export default function AuditLog() {
  const { users } = useApp();
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");

  const load = async () => setLogs((await api.get("/audit-logs", { params: { limit: 300 } })).data);
  useEffect(() => { load(); }, []);

  const exportAll = async () => {
    try {
      const r = await api.get("/export/all");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `flowtrack-export-${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Exported");
    } catch (e) { toast.error("Export failed"); }
  };

  const filtered = logs.filter((l) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (l.action || "").toLowerCase().includes(s) || (l.entity_id || "").toLowerCase().includes(s);
  });

  return (
    <div className="px-6 py-6 max-w-[1200px]">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Compliance</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Audit log</h1>
        </div>
        <div className="flex gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter…" className="w-56 h-9" data-testid="audit-filter" />
          <Button onClick={exportAll} className="gap-1.5" data-testid="export-all-btn"><Download className="w-4 h-4" /> Export data</Button>
        </div>
      </div>
      {filtered.length === 0 ? (
        <Card className="p-16 text-center">
          <ScrollText className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
          <div className="font-display text-lg font-semibold">No audit entries</div>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2 font-medium">When</th>
                <th className="text-left px-3 py-2 font-medium">Who</th>
                <th className="text-left px-3 py-2 font-medium">Action</th>
                <th className="text-left px-3 py-2 font-medium">Entity</th>
                <th className="text-left px-3 py-2 font-medium">Details</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => {
                const u = users.find((x) => x.id === l.user_id);
                return (
                  <tr key={l.id} className="border-t border-border" data-testid={`audit-row-${l.id}`}>
                    <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString()}</td>
                    <td className="px-3 py-2">{u ? u.name : "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs">{l.action}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">{l.entity_id || "—"}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground max-w-md truncate">{l.details ? JSON.stringify(l.details) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
